<template>
    <v-autocomplete
        v-model="select"
        @update:model-value="onSelect"
        v-model:search="search"
        :items="items"
        :loading="isLoading"
        append-inner-icon=""
        menu-icon=""
        :custom-filter="() => true"
        hide-details
        hide-no-data
        return-object
        density="compact"
        variant="solo"
        persistent-placeholder
        class="search-bar"
    >
        <template #append-inner>
            <v-btn icon @click="clear" size="24">
                <v-icon :icon="appendIcon" size="24" />
            </v-btn>
        </template>
    </v-autocomplete>
</template>

<script setup lang="ts">
import { MenuPageLink } from '@geta/kolumbus-frontend/composables';
import { computed, ref, watch } from 'vue';
import { VAutocomplete, VBtn, VIcon } from 'vuetify/components';
import { useSearchService } from '../../composables/SearchService';

interface Props {
    modelValue?: string;
    autocompleteLink?: string;
    searchPage?: MenuPageLink;
}

interface Emits {
    (e: 'update:modelValue', value?: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const appendIcon = computed(() => (search.value || props.modelValue ? 'mdi-close' : 'mdi-magnify'));
const entries = ref<string[]>([]);
const isLoading = ref(false);
const search = ref('');
const select = ref<string | undefined>();
const items = computed(() => entries.value);

const searchService = useSearchService(props.autocompleteLink);

let abortController: AbortController | undefined;
async function querySelections(keyword: string) {
    abortController?.abort();

    if (!keyword) return;

    try {
        isLoading.value = true;
        abortController = new AbortController();
        entries.value = await searchService.getAutocomplete(keyword, { signal: abortController?.signal });
    } catch (e) {
        console.error(e);
    } finally {
        isLoading.value = false;
    }
}

watch(search, value => {
    // if search value is the same as selected item, do nothing
    if (value === select.value) return;

    querySelections(value);
});

function onSelect(value: string) {
    if (!value) return;
    window.location.href = props.searchPage?.link + value;
}

function clear() {
    search.value = '';
    select.value = undefined;
    entries.value = [];
    emit('update:modelValue', undefined);
}
</script>

<style lang="scss">
// placeholders are not persisted always
// https://github.com/vuetifyjs/vuetify/issues/11553
.search-bar {
    &.v-autocomplete .v-field:not(.v-field--focused) input {
        max-height: 25px !important;
        opacity: 1;
    }
}
</style>
